/**
 * Selects which fleet-graphql-api deployment the app sends GraphQL requests to
 * (Apollo client, the asset-index worker, and the `fetchAssetById` fallback).
 *
 * Two deployments serve the same GraphQL contract while asset reads move to
 * the `fc-assets-materialised` index:
 *
 *  - `legacy` — `fleet-graphql-api` (`NEXT_PUBLIC_GRAPHQL_API`).
 *  - `usdp`   — `fleet-graphql-api-usdp`, built from the fleet-command
 *               `fleet-graphql-api-usdp-migration` branch
 *               (`NEXT_PUBLIC_GRAPHQL_API_USDP`).
 *
 * The LaunchDarkly flag `EnableUsdpFleetGraphqlApi` (surfaced by the React SDK
 * as `enableUsdpFleetGraphqlApi`) targets users or companies at the usdp
 * deployment; OFF (or absent / not yet synced) keeps them on legacy. The
 * selection is pinned once per page load, so a flip takes effect on the next
 * load. If the flag is ON but the usdp URL is not configured we fall back to
 * legacy and surface the reason.
 */
export type FleetGraphqlApiService = "legacy" | "usdp";

export type FleetGraphqlApiFallbackReason = "usdp_url_missing";

export interface FleetGraphqlApiSelection {
  url: string;
  service: FleetGraphqlApiService;
  /** Set only when the flag asked for usdp but we had to fall back. */
  fallbackReason?: FleetGraphqlApiFallbackReason;
}

export interface ResolveFleetGraphqlApiArgs {
  /** Snapshot of the `EnableUsdpFleetGraphqlApi` LD flag. */
  enableUsdpFleetGraphqlApi: boolean | undefined;
  /** `NEXT_PUBLIC_GRAPHQL_API` */
  legacyUrl: string;
  /** `NEXT_PUBLIC_GRAPHQL_API_USDP` */
  usdpUrl: string | undefined;
}

const isConfigured = (url: string | undefined): url is string =>
  typeof url === "string" && url.trim().length > 0;

export const resolveFleetGraphqlApi = ({
  enableUsdpFleetGraphqlApi,
  legacyUrl,
  usdpUrl,
}: ResolveFleetGraphqlApiArgs): FleetGraphqlApiSelection => {
  if (enableUsdpFleetGraphqlApi !== true) {
    return { url: legacyUrl, service: "legacy" };
  }
  if (!isConfigured(usdpUrl)) {
    return {
      url: legacyUrl,
      service: "legacy",
      fallbackReason: "usdp_url_missing",
    };
  }
  return { url: usdpUrl, service: "usdp" };
};
